import {useEffect} from 'react';
import {XIcon} from 'lucide-react';
import clsx from 'clsx';
import {ModalProps} from './types';
import ErrorIcon from '../../assets/icons/error.svg?react';
import {cn} from '@chekin/guestapp-ui-kit';
import {IconWrapper, Title, Text, CloseButton, ModalStyled, StyledLottie} from './styled';

function ModalContent({
  iconAlt,
  iconSrc,
  title,
  text,
  iconProps = {},
  isConnectionIssue,
  iconElement,
  iconLottie,
  children,
}: ModalProps) {
  return (
    <>
      {(iconElement || iconSrc || iconProps?.src || iconLottie) && (
        <IconWrapper className="modal__icon">
          {isConnectionIssue && (
            <ErrorIcon width="180" height="180" viewBox="0 0 230 229" />
          )}
          {iconElement}
          {!isConnectionIssue && (
            <>
              {!iconLottie ? (
                <img src={iconSrc} alt={iconAlt} {...iconProps} />
              ) : (
                <StyledLottie animationData={iconLottie} />
              )}
            </>
          )}
        </IconWrapper>
      )}
      {title && <Title className="modal__title">{title}</Title>}
      {text && <Text className="modal__description">{text}</Text>}
      {children}
    </>
  );
}

function Modal({
  open,
  onClose,
  children,
  closeOnDocumentClick = false,
  closeOnEscape = false,
  maxWidth = 500,
  iconAlt,
  iconSrc,
  title,
  text,
  lockScroll = true,
  iconProps = {},
  withCloseButton,
  width = 'auto',
  isConnectionIssue,
  iconElement,
  iconLottie,
  className,
  ...props
}: ModalProps) {
  useEffect(() => {
    if (open && window.ChekinProSettings?.autoHeight) {
      window.scrollTo({top: 0});
    }
  }, [open]);

  return (
    <ModalStyled
      open={open}
      onClose={onClose}
      closeOnDocumentClick={closeOnDocumentClick}
      closeOnEscape={closeOnEscape}
      lockScroll={lockScroll}
      maxWidth={maxWidth}
      width={width}
      className={clsx(
        className,
        'modal',
        'kiosko:left-0 kiosko:max-w-[996px] kiosko:rounded-[36px] kiosko:p-14',
      )}
      {...props}
    >
      <>
        {withCloseButton && (
          <CloseButton
            onClick={onClose}
            role="button"
            className={clsx('modal__close-bt')}
          >
            <XIcon
              className={clsx(
                'w-3 h-3',
                'kiosko:w-10 kiosko:h-10 text-[var(--font-primary)]',
              )}
            />
          </CloseButton>
        )}
        {(iconElement || iconSrc || iconProps?.src || iconLottie) && (
          <IconWrapper className="modal__icon">
            {isConnectionIssue && (
              <ErrorIcon width="180" height="180" viewBox="0 0 230 229" />
            )}
            {iconElement}
            {!isConnectionIssue && (
              <>
                {!iconLottie ? (
                  <img
                    src={iconSrc}
                    alt={iconAlt}
                    {...iconProps}
                    className={cn(iconProps?.className)}
                  />
                ) : (
                  <StyledLottie animationData={iconLottie} />
                )}
              </>
            )}
          </IconWrapper>
        )}
        {title && <Title className="modal__title kiosko:!text-kiosko-l">{title}</Title>}
        {text && (
          <Text className="modal__description kiosko:!text-kiosko-m kiosko:max-w-[600px]">
            {text}
          </Text>
        )}
        {children}
      </>
    </ModalStyled>
  );
}
export {Modal, ModalContent};
